export enum StorageKeys {
  GxToken = 'auth.gxToken',
  TripToken = 'auth.tripToken',
  TokenExpiry = 'auth.tokenExpiry',
  Impersonator = 'auth.impersonator',
  CachedToken = 'auth.cachedToken',
  LastPathname = 'app.lastPathname',
  DashboardEditLock = 'dashboard.editLock',
  TabId = 'session.tabId',
  DismissedArticles = 'market.dismissedArticles',
  SpotHistoryColumnWidths = 'ui.spotHistoryColumnWidths'
}

export type StorageMap = {
  'auth.gxToken': string;
  'auth.tripToken': string;
  'auth.tokenExpiry': number;
  'auth.impersonator': string;
  'auth.cachedToken': string;
  'app.lastPathname': string;
  'dashboard.editLock': { tabId: string };
  'session.tabId': string;
  'market.dismissedArticles': { articleId: string; publishedAt: number }[];
  'ui.spotHistoryColumnWidths': Record<string, Record<string, number>>;
};
export type StorageKey = keyof StorageMap;

const PREFIX = 'gxgo:';
const SAME_TAB = 'gxgo:storage';

const SESSION_KEYS = new Set<StorageKey>(['auth.tripToken', 'session.tabId']);
const storageFor = (key: StorageKey): Storage =>
  SESSION_KEYS.has(key) ? sessionStorage : localStorage;

export const storage = {
  get<K extends StorageKey>(key: K): StorageMap[K] | null {
    const raw = storageFor(key).getItem(PREFIX + key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as StorageMap[K];
    } catch {
      storageFor(key).removeItem(PREFIX + key);
      return null;
    }
  },

  set<K extends StorageKey>(key: K, value: StorageMap[K]): void {
    storageFor(key).setItem(PREFIX + key, JSON.stringify(value));
    window.dispatchEvent(new CustomEvent(SAME_TAB, { detail: key }));
  },

  remove(key: StorageKey): void {
    storageFor(key).removeItem(PREFIX + key);
    window.dispatchEvent(new CustomEvent(SAME_TAB, { detail: key }));
  },

  subscribe<K extends StorageKey>(key: K, fn: (value: StorageMap[K] | null) => void): () => void {
    const onCross = (e: StorageEvent) => {
      if (e.key === PREFIX + key) fn(storage.get(key));
    };
    const onSame = (e: Event) => {
      if ((e as CustomEvent).detail === key) fn(storage.get(key));
    };
    window.addEventListener('storage', onCross);
    window.addEventListener(SAME_TAB, onSame);
    return () => {
      window.removeEventListener('storage', onCross);
      window.removeEventListener(SAME_TAB, onSame);
    };
  }
};
